import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Capacitor } from '@capacitor/core';

import { messages as openNativeSettingMessages } from '@ocx/feat-store-locator';
import { useInfoActionSheet, useCompleteEmailActionSheet } from '@ocx/feat-modals';
import { useOpenSystemSettings } from '@ocx/utils-device';
import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';

import { messages } from '../../bottom-navigation.messages';

export const useAlerts = () => {
  const { pushToStoreLocator } = useGetNavigationActions();
  const { open: openCompleteEmailActionSheet } = useCompleteEmailActionSheet();
  const snackbar = useSnackbar();
  const { open: openInfoActionSheet } = useInfoActionSheet();
  const intl = useIntl();

  const openNativeSettings = useOpenSystemSettings();

  const showNoGeolocationAvailableAlert = useCallback(
    () =>
      openInfoActionSheet({
        title: intl.formatMessage(openNativeSettingMessages['storeLocator:provideLocationPopupTitle']),
        description: intl.formatMessage(openNativeSettingMessages['storeLocator:provideLocationPopupDescription']),
        actionButtonProps: Capacitor.isNativePlatform()
          ? {
              actionButtonText: intl.formatMessage(
                openNativeSettingMessages['storeLocator:provideLocationPopupConfirmButton'],
              ),
              onActionButtonClick: () => openNativeSettings('application_details'),
            }
          : undefined,
        isServiceLinkEnabled: true,
      }),
    [intl, openInfoActionSheet, openNativeSettings],
  );

  const showNoStoreFoundAlert = useCallback(
    () =>
      openInfoActionSheet({
        title: intl.formatMessage(messages['notClosePayAtPumpPopup:title']),
        description: intl.formatMessage(messages['notClosePayAtPumpPopup:description']),
        actionButtonProps: {
          actionButtonText: intl.formatMessage(messages['notClosePayAtPumpPopup:actionButtonText']),
          onActionButtonClick: pushToStoreLocator,
        },
        isServiceLinkEnabled: true,
      }),
    [intl, openInfoActionSheet, pushToStoreLocator],
  );

  const showNoMobilePaymentAlert = useCallback(() => {
    openInfoActionSheet({
      title: intl.formatMessage(messages['cantPayAtPumpPopup:title']),
      description: intl.formatMessage(messages['cantPayAtPumpPopup:description']),
      actionButtonProps: {
        actionButtonText: intl.formatMessage(messages['cantPayAtPumpPopup:actionButtonText']),
        onActionButtonClick: pushToStoreLocator,
      },
      isServiceLinkEnabled: true,
    });
  }, [intl, openInfoActionSheet, pushToStoreLocator]);

  const showCompleteEmailAlert = useCallback(
    (params: { onCompleted?(): void } = {}) => {
      openCompleteEmailActionSheet({
        onCompleted: params.onCompleted,
      });
    },
    [openCompleteEmailActionSheet],
  );

  const showNoConnectionAlert = useCallback(() => {
    snackbar.open(intl.formatMessage(messages['noConnectionPopup:noConnectionText']), { variant: 'error' });
  }, [intl, snackbar]);

  return useMemo(
    () => ({
      showNoGeolocationAvailableAlert,
      showNoStoreFoundAlert,
      showNoConnectionAlert,
      showNoMobilePaymentAlert,
      showCompleteEmailAlert,
    }),
    [
      showNoMobilePaymentAlert,
      showNoConnectionAlert,
      showNoGeolocationAvailableAlert,
      showNoStoreFoundAlert,
      showCompleteEmailAlert,
    ],
  );
};
